<template>
    <div class="wse animated fadeIn" v-if="workspace !== undefined">
        <!-- Page Header -->
        <div class="wse-page-header">
            <button class="wse-back-btn" @click="$router.back()">
                <b-icon icon="arrow-left" class="mr-1"></b-icon>
                {{ $t('back') }}
            </button>
        </div>

        <!-- Main Card -->
        <div>
            <div class="wse-card">
                <!-- Card Header -->
                <div class="wse-card-header">
                    <div class="wse-card-icon">
                        <span class="wse-initial">{{ getInitial(workspace.name) }}</span>
                    </div>
                    <div class="wse-header-info">
                        <h2 class="wse-card-title">{{ $t('editWorkspace') }}</h2>
                        <p class="wse-card-name-label">{{ workspace.name }}</p>
                    </div>
                </div>

                <!-- Form Body -->
                <div class="wse-card-body">
                    <b-form>
                        <InputField
                            name="name"
                            :label="$t('name')"
                            v-model="workspace.name"
                            @error="fieldError"
                            :rules="{ required: false, length: { min: 2 } }"
                        />

                        <div class="wse-form-actions">
                            <button type="button" class="wse-save-btn" @click="edit">
                                <b-icon icon="check2" class="mr-2"></b-icon>
                                {{ $t('save') }}
                            </button>
                        </div>
                    </b-form>
                </div>

                <!-- Danger Zone -->
                <div class="wse-danger-zone" v-if="workspaces.length > 1">
                    <div class="wse-danger-header">
                        <b-icon icon="exclamation-triangle" class="mr-2"></b-icon>
                        {{ $t('dangerZone') }}
                    </div>
                    <div class="wse-danger-body">
                        <div class="wse-danger-info">
                            <p class="wse-danger-warning">{{ $t('deleteWorkspaceWarning') }}</p>
                        </div>
                        <button type="button" class="wse-delete-btn" @click="remove">
                            <b-icon icon="trash3" class="mr-2"></b-icon>
                            {{ $t('delete') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Confirm Delete Dialog -->
        <main-dialog
            :show="confirmDialog.show"
            :title="$t('delete')"
            @close="confirmDialog.show = false"
        >
            <p>{{ $t('cantRevert') }}</p>
            <template #footer>
                <b-button variant="outline-secondary" @click="confirmDialog.show = false">{{ $t('cancel') }}</b-button>
                <b-button variant="danger" @click="doDelete">{{ $t('delete') }}</b-button>
            </template>
        </main-dialog>

        <!-- Error Dialog -->
        <main-dialog
            :show="errorDialog.show"
            :title="$t('error')"
            @close="errorDialog.show = false"
        >
            <p>{{ errorDialog.message }}</p>
            <template #footer>
                <b-button variant="secondary" @click="errorDialog.show = false">{{ $t('close') }}</b-button>
            </template>
        </main-dialog>
    </div>
</template>

<style scoped lang="scss">
.wse {
    padding: 0;
}

/* ── Page Header ───────────────────────────────────────────────────── */
.wse-page-header {
    margin-bottom: 1.75rem;
}

.wse-back-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.45rem 0.9rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-secondary);
    background: var(--bg-card);
    border: 1px solid var(--border-default);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        color: var(--text-primary);
        border-color: var(--border-hover);
        background: var(--bg-card-secondary);
    }
}

/* ── Card ──────────────────────────────────────────────────────────── */
.wse-card {
    width: 100%;
    max-width: 520px;
    background: var(--bg-card);
    border: 1px solid var(--border-default);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
}

/* ── Card Header ───────────────────────────────────────────────────── */
.wse-card-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem 1.75rem;
    background: linear-gradient(135deg, rgba(94, 231, 223, 0.08) 0%, rgba(180, 144, 202, 0.08) 100%);
    border-bottom: 1px solid var(--border-default);
}

.wse-card-icon {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: linear-gradient(135deg, #5ee7df 0%, #b490ca 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(94, 231, 223, 0.3);
}

.wse-initial {
    font-size: 1.25rem;
    font-weight: 700;
    color: #fff;
    line-height: 1;
}

.wse-header-info {
    flex: 1;
    min-width: 0;
}

.wse-card-title {
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 0.2rem;
    line-height: 1.2;
}

.wse-card-name-label {
    font-size: 0.85rem;
    color: var(--text-secondary);
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* ── Card Body ─────────────────────────────────────────────────────── */
.wse-card-body {
    padding: 1.75rem;
}

/* ── Form Actions ──────────────────────────────────────────────────── */
.wse-form-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1.5rem;
    padding-top: 1.25rem;
    border-top: 1px solid var(--border-default);
}

.wse-save-btn {
    display: inline-flex;
    align-items: center;
    padding: 0.65rem 1.5rem;
    font-size: 0.9rem;
    font-weight: 600;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--primary-start), var(--primary-end));
    color: #fff;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(102, 126, 234, 0.35);
    transition: all 0.25s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
    }

    &:active {
        transform: translateY(0);
    }
}

/* ── Danger Zone ───────────────────────────────────────────────────── */
.wse-danger-zone {
    border-top: 1px solid rgba(220, 53, 69, 0.25);
    background: rgba(220, 53, 69, 0.04);
}

.wse-danger-header {
    display: flex;
    align-items: center;
    padding: 0.9rem 1.75rem;
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: rgba(220, 53, 69, 0.8);
    border-bottom: 1px solid rgba(220, 53, 69, 0.15);
}

.wse-danger-body {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    padding: 1.25rem 1.75rem;
}

.wse-danger-warning {
    font-size: 0.85rem;
    color: var(--text-secondary);
    margin: 0;
    line-height: 1.5;
}

.wse-delete-btn {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    padding: 0.55rem 1.25rem;
    font-size: 0.875rem;
    font-weight: 600;
    border: 1px solid rgba(220, 53, 69, 0.5);
    border-radius: 10px;
    background: rgba(220, 53, 69, 0.08);
    color: rgb(220, 53, 69);
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
        background: rgba(220, 53, 69, 0.15);
        border-color: rgb(220, 53, 69);
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
    }
}
</style>

<script>
import Input from "@/components/form/Input"

export default {
    name: "Edit",
    components: {
        InputField: Input
    },
    data: function () {
        return {
            errors: [],
            confirmDialog: { show: false },
            errorDialog: { show: false, message: '' }
        }
    },
    computed: {
        workspace() {
            return this.$store.getters.getCurrentProject.workspaces.find(w => {
                return w.uuid == this.$route.params.id
            })
        },

        workspaces() {
            return this.$store.getters.getCurrentProject.workspaces
        }
    },

    methods: {
        getInitial(name) {
            if (!name || name.length === 0) return '?'
            return name[0].toUpperCase()
        },

        remove() {
            this.confirmDialog.show = true
        },

        doDelete() {
            this.confirmDialog.show = false
            this.$http.delete("/v1/project/workspace/" + this.$route.params.id).then(resp => {
                this.$store.commit("DELETE_WORKSPACE", this.workspace)
                this.$router.back()

                this.$bvToast.toast("Workspace removed", {
                    variant: 'danger',
                    title: "Error"
                })
            }).catch(error => {
                if (error.response !== undefined) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }
                    this.errorDialog.message = error.response.data.message
                    this.errorDialog.show = true
                }
            })
        },

        edit() {
            const popup = this.$loadingPopup()
            this.$http.put("/v1/project/workspace/" + this.$route.params.id, this.workspace).catch(error => {
                if (error.response !== undefined) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }
                    this.errorDialog.message = error.response.data.message
                    this.errorDialog.show = true
                }
            }).finally(() => {
                popup.hide()
            })
        },

        fieldError(error) {
            let found = this.errors.find((i, e) => {
                return i.field == error.field
            })

            if (found === undefined) {
                this.errors.push(error)
            } else {
                this.errors.map(e => {
                    if (found.field == e.field)
                        e.error = error.error
                })
            }
        },

        checkForErrors() {
            return this.errors.find((i, e) => {
                return i.error
            })
        }
    },
    mounted() {
        if (!this.$isAdmin()) {
            this.$router.back()
        }

        if (this.workspace === null || this.workspace === undefined) {
            this.$router.back()
        }
    }
}
</script>
